import { useState, useEffect } from 'react';
import { formatCurrency } from '@atc/string-fns';
import { batch } from 'react-redux';
import clsx from 'clsx';
import { InfoIcon } from 'reaxl-molecules';
import { Text, ModalTrigger, Button } from 'reaxl';
import { TaxesFeesModal } from 'reaxl-wallet';
import _isEmpty from 'lodash/isEmpty';
import { jsxs, jsx } from 'react/jsx-runtime';
function TaxesAndFeesDetail(_ref) {
  let {
    includeTaxesAndFees,
    isNationalRates,
    isSnapshot,
    taxModalTrigger,
    totalTaxesFees,
    fontSize
  } = _ref;
  if (isNationalRates || isSnapshot && !includeTaxesAndFees) {
    return /*#__PURE__*/jsx(Text, {
      componentClass: "div",
      className: "text-overflow",
      color: "subdued",
      children: "Not Available"
    });
  }
  if (includeTaxesAndFees) {
    return /*#__PURE__*/jsx("div", {
      className: "col-xs-5 col-md-4 padding-0 flex-shrink-0",
      children: totalTaxesFees !== 0 ? /*#__PURE__*/jsxs(Text, {
        size: fontSize,
        componentClass: "div",
        className: "text-right",
        color: "subdued",
        children: ["+ ", formatCurrency(totalTaxesFees)]
      }) : /*#__PURE__*/jsx(Text, {
        size: fontSize,
        componentClass: "div",
        className: "text-right",
        color: "subdued",
        children: "Check with dealer"
      })
    });
  }
  return /*#__PURE__*/jsx("div", {
    className: "text-overflow",
    children: taxModalTrigger(false)
  });
}

/**
 * Component that handles the display and management of taxes and fees in the deal calculator
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} [props.activeTab='finance'] - Currently active tab (finance, lease, cash)
 * @param {string} props.brand - Brand identifier (e.g., 'kbb')
 * @param {Object} props.container - Container for Syndicated Calculator
 * @param {string} props.isEstimate - Determines if the 'Est." label is shown
 * @param {boolean} [props.enableTaxesAndFees=false] - Whether taxes and fees functionality is enabled
 * @param {Function} [props.fetchPaymentDetails=()=>{}] - Function to fetch payment details
 * @param {Function} [props.handleNDSClick=()=>{}] - Function to handle NDS click events
 * @param {Function} [props.handleNDSPencilling=()=>{}] - Function to handle NDS pencilling events
 * @param {boolean} [props.includeTaxesAndFees=false] - Whether taxes and fees are included in calculation
 * @param {boolean} props.isNationalRates - Whether national rates are being used
 * @param {string} props.ndsPencilingFeature - NDS penciling feature identifier
 * @param {Object} props.paymentCalculationDetails - Payment calculation details object
 * @param {Function} [props.setIncludeTaxesAndFees=()=>{}] - Function to update includeTaxesAndFees state
 * @param {Function} [props.setPaymentCalculationDetails=()=>{}] - Function to update payment calculation details
 * @param {Function} [props.setHasTaxesFeesModalOpen=()=>{}] - Function to set modal open state
 * @param {string} [props.tabName='finance'] - Name of the current tab
 * @param {Object} [props.taxesAndFeesObj={finance:{},lease:{},cash:{}}] - Object containing taxes and fees data
 * @param {number} props.totalTaxesFees - Total taxes and fees amount
 * @param {string} [props.uiContext='default'] - UI context ('default', 'offerBreakdown', or 'snapshot')
 * @param {Function} [props.updatePaymentPencils=()=>{}] - Function to update payment pencils
 * @param {Function} [props.updateUserPaymentInfo=()=>{}] - Function to update user payment information
 * @param {string} props.zip - ZIP code for tax calculation
 * @returns {React.ReactElement|null} The taxes and fees component or null if disabled
 */
function TaxesAndFees(_ref2) {
  let {
    activeTab = 'finance',
    brand,
    container,
    // For Syndicated Calc
    isEstimate,
    enableTaxesAndFees = false,
    fetchPaymentDetails = () => {},
    handleNDSClick = () => {},
    handleNDSPencilling = () => {},
    includeTaxesAndFees = false,
    // We can't move includeTaxesAndFees to configContext as it is UDS state variable but not a UDS config value.
    isNationalRates,
    ndsPencilingFeature,
    paymentCalculationDetails,
    setIncludeTaxesAndFees = () => {},
    setPaymentCalculationDetails = () => {},
    setHasTaxesFeesModalOpen = () => {},
    tabName = 'finance',
    taxesAndFeesObj = {
      finance: {},
      lease: {},
      cash: {}
    },
    totalTaxesFees,
    uiContext = 'default',
    updatePaymentPencils = () => {},
    updateUserPaymentInfo = () => {},
    zip,
    onNdsInitialClick = () => {},
    suppressTaxesAndFeesCta = false
  } = _ref2;
  const [showTaxesFeesModal, setShowTaxesFeesModal] = useState(false);
  const [isLoadingTaxes, setIsLoadingTaxes] = useState(false);
  const [hasError, setHasError] = useState(false);
  useEffect(() => {
    if (isLoadingTaxes && !_isEmpty(taxesAndFeesObj[activeTab])) {
      setIsLoadingTaxes(false);
      setHasError(false);
    } else if (_isEmpty(taxesAndFeesObj[activeTab])) {
      setIsLoadingTaxes(false);
      setHasError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxesAndFeesObj[activeTab]]); // we need to check only for taxesAndFeesObj for state change.

  if (!enableTaxesAndFees) {
    return null;
  }
  const isKbb = brand === 'kbb';
  const isOfferBreakdown = uiContext === 'offerBreakdown';
  const isSnapshot = uiContext === 'snapshot';
  const fontSize = isOfferBreakdown || isSnapshot ? 300 : 400;
  const handleTaxesFeesModalOpen = event => {
    const userModified = 'TAXES_AND_FEES_MODAL_OPENED';
    const eventData = {
      feature: ndsPencilingFeature,
      cmp: isKbb ? 'see_dtls' : 'tf_see_dtls',
      eventResult: 'see-details',
      userModified,
      inputName: 'taxesAndFee'
    };
    if (event?.target?.tagName?.toLowerCase() === 'button') {
      eventData.eventResult = 'taxes-fees';
      eventData.cmp = 'g_tax_fee';
    }
    onNdsInitialClick(event);
    handleNDSPencilling(event, eventData);
    setIsLoadingTaxes(true);
    handleNDSClick(event, {
      userModified,
      elementText: 'Get Taxes & Fees'
    });
    setShowTaxesFeesModal(true);
    setHasTaxesFeesModalOpen(true);
    fetchPaymentDetails(true, false, zip.toString());
  };
  const handleTaxesFeesModalHide = () => {
    setShowTaxesFeesModal(false);
    setHasTaxesFeesModalOpen(false);
  };
  const handleUpdatingTaxZip = updatedZip => {
    setIsLoadingTaxes(true);
    fetchPaymentDetails(true, false, updatedZip.toString());
  };
  const handleSavingTaxPref = (updatedZip, event) => {
    handleNDSPencilling(event, {
      cmp: isKbb ? 'ctn_btn' : 'tf_ctn_btn',
      eventResult: 'continue',
      feature: ndsPencilingFeature
    });
    batch(() => {
      if (updatedZip) {
        updateUserPaymentInfo('hasRequestedTaxesFees', true, updatedZip);
      }
      if (!includeTaxesAndFees) {
        setIncludeTaxesAndFees(true);
      }
      setPaymentCalculationDetails({
        ...paymentCalculationDetails,
        apply: true
      });
      // This resets the customLender object so that the payment details, lending options modal,
      // and send offer lead form all have the same lender after adding taxes
      updatePaymentPencils({
        customLender: {}
      });
    });
  };
  const taxModalTrigger = isLink => tabName === activeTab && /*#__PURE__*/jsx(ModalTrigger, {
    showModal: showTaxesFeesModal,
    onClick: handleTaxesFeesModalOpen,
    modal: /*#__PURE__*/jsx(TaxesFeesModal, {
      activeTab: activeTab,
      container: container,
      showModal: showTaxesFeesModal,
      zip: zip,
      taxesFees: taxesAndFeesObj?.[activeTab],
      onHide: handleTaxesFeesModalHide,
      onContinue: handleSavingTaxPref,
      onUpdate: handleUpdatingTaxZip,
      isEstimate: isEstimate,
      isLoading: isLoadingTaxes,
      hasError: hasError
    }),
    children: isLink ? /*#__PURE__*/jsx(Button, {
      bsStyle: "text",
      onClick: handleTaxesFeesModalOpen,
      children: "See Details"
    }) : /*#__PURE__*/jsx(Button, {
      bsStyle: "default",
      bsSize: "small",
      children: "Get Taxes & Fees"
    })
  });
  const taxesAndFeesText = /*#__PURE__*/jsxs(Text, {
    className: "display-flex flex-wrap gap-1",
    color: "subdued",
    size: fontSize,
    children: [isEstimate ? 'Est. Taxes & Fees' : 'Taxes & Fees', !isOfferBreakdown && /*#__PURE__*/jsx(InfoIcon, {
      id: `taxes-and-fees-info-${uiContext}`,
      infoText: "Usually includes sales tax, DMV fees, any lender fees for financing, plus dealer-related fees for doc and prep",
      placement: "top"
    })]
  }, "taxesAndFeesText");
  return /*#__PURE__*/jsxs("div", {
    className: clsx('display-flex justify-content-between margin-bottom-4', {
      'align-items-center': !isOfferBreakdown,
      'align-items-top': isOfferBreakdown
    }),
    children: [/*#__PURE__*/jsxs("div", {
      className: "display-block justify-content-between align-items-center display-flex flex-wrap gap-2",
      children: [!suppressTaxesAndFeesCta && taxesAndFeesText, !isSnapshot && includeTaxesAndFees && !isNationalRates && taxModalTrigger(true)]
    }), !suppressTaxesAndFeesCta && /*#__PURE__*/jsx(TaxesAndFeesDetail, {
      includeTaxesAndFees: includeTaxesAndFees,
      isNationalRates: isNationalRates,
      isSnapshot: isSnapshot,
      taxModalTrigger: taxModalTrigger,
      totalTaxesFees: totalTaxesFees,
      fontSize: fontSize,
      activeTab: activeTab
    })]
  }, "taxAndFee");
}
export { TaxesAndFees as default };
